<template>
  <div class="p-6 bg-white rounded-lg">
    <div class="flex items-center mb-4 pb-2 border-b">
      <div
        class="w-12 h-12 rounded-lg text-white cursor-pointer"
        :class="this.module.settings.icon_color ? this.module.settings.icon_color : 'bg-tertiary'"
        @click="iconPickerVisible = true"
      >
        <icon
          v-if="this.module.settings.icon"
          :name="this.module.settings.icon"
          class="text-white"
        />
      </div>
      <h3 class="ml-4 text-2xl font-medium">
        Related Media
      </h3>
    </div>

    <input
      type="text"
      placeholder="Title"
      class="input-sm w-full mb-2"
      v-model="module.title"
    >

    <textarea
      rows="3"
      placeholder="Intro Text"
      class="input-sm w-full mb-4"
      v-model="module.settings.intro"
    />

    <div class="mb-6 pt-6 border-t">
      <h4 class="mb-4 font-semibold">
        Downloadable Content:
      </h4>

      <div
        class="mb-4 flex flex-wrap"
        v-for="(content, index) in downloadable"
        :key="index"
      >
        <div
          class="w-full md:w-auto md:flex-1 md:mr-2"
        >
          <div class="absolute pin-r bg-white rounded-full border h-6 flex text-xs overflow-hidden">
            <button
              type="button"
              class="w-6 h-6 hover:bg-grey-lighter" :disabled="index==0"
              @click="moveDownloadable(index,index-1)"
            >
              <i class="fas fa-chevron-up"></i>
            </button>
            <button
              type="button"
              class="w-6 h-6 hover:bg-grey-lighter" :disabled="index==(content.length-1)" @click="moveDownloadable(index,index+1)"
            >
              <i class="fas fa-chevron-down"></i>
            </button>
            <button
              type="button"
              class="w-6 h-6 hover:bg-grey-lighter"
              @click.stop="removeDownloadable(index)"
            >
              <i class="fas fa-times" />
            </button>
          </div>
          <div
            class="relative w-36 h-48 flex justify-center items-end bg-grey bg-cover bg-center bg-no-repeat border cursor-pointer mt-8"
            :style="{ 'background-image': `url(${content.path})` }"
            @click="selectedContent = index; mediaManagerVisible = true"
          >
            <div class="relative flex flex-col items-center mb-4 text-sm text-white font-bold">
              <i
                v-if="content.filetypeIcon"
                class="fas fa-6x mb-4"
                :class="content.filetypeIcon"
              />
              Click to change
            </div>
          </div>
        </div>
        <div
          class="w-full md:w-auto md:flex-1 mt-8"
        >
          <label
            class="label-sm"
          >
            Filename:
          </label>
          <input
            class="input-sm w-full"
            v-model="content.filename"
            :disabled="!content.path"
          >
          <label
            class="label-sm mt-4"
          >
            Description:
          </label>
          <textarea
            rows="4"
            class="input-sm w-full"
            v-model="content.description"
          />
        </div>
      </div>

      <button
        @click="addDownloadable"
        type="button"
        class="mt-6 mx-auto px-6 py-1 flex items-center justify-center text-sm text-grey-darkest rounded-lg shadow hover:shadow-none border border-blue bg-white"
      >
        <span class="text-lg mr-2">&#43;</span>
        Add more downloads
      </button>
    </div> <!-- end of downloadable -->

    <div class="mb-6 pt-6 border-t">
      <h4 class="mb-4 font-semibold">
        Video Content:
      </h4>

      <div
        class="mb-4 flex flex-wrap"
        v-for="(video, index) in videos"
        :key="index"
      >
        <div class="relative w-full">
          <button
            v-if="index > 0"
            type="button"
            class="absolute pin-t pin-r bg-white -mt-2 -mr-2 rounded-full border h-6 w-6 flex justify-center items-center z-10"
            @click.stop="removeVideo(index)"
          >
            <i class="fas fa-times fa-fw fa-xs" />
          </button>
          <input
            type="text"
            class="input-sm w-full mb-4"
            v-model="video.title"
            placeholder="Video title here"
          >
          <input
            type="url"
            class="input-sm w-full"
            v-model="video.src"
            placeholder="Paste URL here..."
          >
        </div>
      </div>

      <button
        @click="addVideo"
        type="button"
        class="mt-6 mx-auto px-6 py-1 flex items-center justify-center text-sm text-grey-darkest rounded-lg shadow hover:shadow-none border border-blue bg-white"
      >
        <span class="text-lg mr-2">&#43;</span>
        Add more video links
      </button>
    </div> <!-- end of video -->

    <div
      class="mb-6 pt-6 border-t"
    >
      <textarea
        rows="3"
        placeholder="Outro Text"
        class="input-sm w-full mb-4"
        v-model="module.settings.outro"
      />
    </div>

    <modal v-if="iconPickerVisible" :scrollY="false">
      <icon-picker />
    </modal>

    <modal
      v-if="mediaManagerVisible"
    >
      <media-manager
        @selected="selectMedia($event)"
        @closeMediaManager="mediaManagerVisible = false; selectedContent = null"
      />
    </modal>
  </div>
</template>

<script>
import { EventBus } from '@/EventBus'
import IconPicker from '@/components/IconPicker'

export default {
  components: { IconPicker },

  props: [ 'module' ],

  computed: {
    settings () {
      return this.module.settings
    },

    downloadable () {
      return this.settings.contents
    },

    videos () {
      return this.settings.videos
    }
  },

  data () {
    return {
      mediaManagerVisible: false,
      iconPickerVisible: false,
      selectedContent: null
    }
  },

  methods: {
    selectMedia (media) {
      this.downloadable[this.selectedContent] = media
      this.mediaManagerVisible = false
    },

    addDownloadable () {
      this.downloadable.push({ path: null, filename: null, description: null })
    },

    removeDownloadable (index) {
      this.downloadable.splice(index, 1)
    },

    addVideo () {
      this.videos.push({ src: null })
    },

    removeVideo (index) {
      this.videos.splice(index, 1)
    },

    selectIcon (data) {
      if (!this.iconPickerVisible) {
        return
      }

      this.iconPickerVisible = false

      this.$set(this.module.settings, 'icon', data.icon)
      this.$set(this.module.settings, 'icon_color', data.color)
    },

    cancelIcon () {
      this.iconPickerVisible = false
    },
    moveDownloadable (from, to) {
      this.downloadable.splice(to, 0, this.downloadable.splice(from, 1)[0])
    }
  },

  created () {
    EventBus.$on('icon-picker:cancel', this.cancelIcon)
    EventBus.$on('icon-picker:saved', this.selectIcon)
  },

  beforeDestroy () {
    EventBus.$off('icon-picker:cancel', this.cancelIcon)
    EventBus.$off('icon-picker:saved', this.selectIcon)
  }
}
</script>
